import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { httpStatusOf } from '@/app/live-election/engine/capability';
import { type IPreviewOp } from '@/app/strong-hold/engine/draft';
import {
	type GapGrain,
	type IChangeResult,
	type ICopyResult,
	type IEstimate,
	type IHistoryPage,
	type IPatchOp,
	type IPlan,
	type IReferenceElection,
	type ISettingsInput,
} from '@/app/strong-hold/types';
import { liveElectionKeys } from '@/hooks/queries/live-election/keys';
import { coreApi, queryApi } from '@/lib/api-client';

import { strongHoldKeys } from './keys';

/** `partyId` goes on the URL only for Super Admin; for everyone else the server reads the token. */
const withParty = (partyId: number | null, params: URLSearchParams = new URLSearchParams()) => {
	if (partyId !== null && partyId > 0) params.set('partyId', String(partyId));
	const s = params.toString();
	return s ? `?${s}` : '';
};

/** 404/405 = this server does not have the route yet: a state, not an error. Never retried. */
const retryUnlessAbsent = (count: number, err: unknown) => {
	const status = httpStatusOf(err);
	return status !== 404 && status !== 405 && status !== 403 && status !== 401 && count < 2;
};

const bodyOf = (err: unknown) => (err as { response?: { data?: { message?: string; data?: unknown } } }).response?.data;

export const isAbsent = (err: unknown) => {
	const s = httpStatusOf(err);
	return s === 404 || s === 405 || s === 503;
};

/** The whole plan: every tier, every turnout override, the settings and whether this caller may write. */
export function useStrongHoldPlan(electionId: number, partyId: number | null, enabled = true) {
	return useQuery({
		queryKey: strongHoldKeys.plan(electionId, partyId ?? 0),
		queryFn: async ({ signal }): Promise<IPlan> => {
			const params = new URLSearchParams({ tiers: 'all' });
			const { data } = await coreApi.get(`/api/v1/LiveElection/${electionId}/strongholds${withParty(partyId, params)}`, {
				signal,
			});
			const plan = data?.data as IPlan;
			// A pre-Strong Hold api.core answers without tiers: read it as "not available yet".
			if (!plan?.settings || !plan.allTiers) throw Object.assign(new Error('outdated'), { response: { status: 405 } });
			return plan;
		},
		enabled: enabled && electionId > 0,
		staleTime: 60_000,
		retry: retryUnlessAbsent,
	});
}

const estimatePath = (electionId: number, partyId: number | null, includeActuals: boolean, grain: GapGrain) => {
	const params = new URLSearchParams({ electionId: String(electionId), grain });
	if (includeActuals) params.set('includeActuals', 'true');
	if (partyId !== null && partyId > 0) params.set('partyId', String(partyId));
	// api.query paths need the `i-elect` segment — the gateway strips `/query` but does not add it.
	return `/api/v1/i-elect/StrongholdPlan/estimate?${params.toString()}`;
};

/** The estimate for the SAVED plan. */
export function useStrongHoldEstimate(args: {
	electionId: number;
	partyId: number | null;
	includeActuals: boolean;
	grain: GapGrain;
	enabled: boolean;
}) {
	const { electionId, partyId, includeActuals, grain, enabled } = args;
	return useQuery({
		queryKey: strongHoldKeys.estimate(electionId, partyId ?? 0, includeActuals, grain),
		queryFn: async ({ signal }): Promise<IEstimate> => {
			const { data } = await queryApi.get(estimatePath(electionId, partyId, includeActuals, grain), { signal });
			return data?.data as IEstimate;
		},
		enabled: enabled && electionId > 0,
		staleTime: 30_000,
		// On election day the actuals move; otherwise the plan only changes when someone saves.
		refetchInterval: includeActuals ? 60_000 : false,
		refetchIntervalInBackground: false,
		retry: retryUnlessAbsent,
		placeholderData: keepPreviousData,
	});
}

/**
 * The estimate over UNSAVED changes. Callers pass already-debounced ops; an empty op list
 * disables it (the saved estimate is the answer).
 */
export function useStrongHoldPreview(args: {
	electionId: number;
	partyId: number | null;
	includeActuals: boolean;
	grain: GapGrain;
	ops: IPreviewOp[];
	enabled: boolean;
}) {
	const { electionId, partyId, includeActuals, grain, ops, enabled } = args;
	const signature = JSON.stringify(ops);
	return useQuery({
		queryKey: [...strongHoldKeys.preview(electionId, partyId ?? 0, signature), includeActuals, grain],
		queryFn: async ({ signal }): Promise<IEstimate> => {
			const { data } = await queryApi.post(
				'/api/v1/i-elect/StrongholdPlan/preview',
				{ electionId, partyId: partyId && partyId > 0 ? partyId : null, includeActuals, grain, ops },
				{ signal }
			);
			return data?.data as IEstimate;
		},
		enabled: enabled && electionId > 0 && ops.length > 0,
		staleTime: 30_000,
		retry: false,
		placeholderData: keepPreviousData,
	});
}

export type SaveOutcome =
	| { kind: 'saved'; result: IChangeResult }
	| { kind: 'invalid'; result: IChangeResult }
	| { kind: 'conflict'; message: string }
	| { kind: 'frozen'; message: string }
	| { kind: 'readOnly'; message: string }
	| { kind: 'error'; message: string };

const classifyWrite = (err: unknown): SaveOutcome => {
	const status = httpStatusOf(err);
	const body = bodyOf(err);
	if (status === 400 && body?.data) return { kind: 'invalid', result: body.data as IChangeResult };
	if (status === 403) return { kind: 'readOnly', message: body?.message ?? '' };
	if (status === 409) {
		const frozen = /locked|frozen/i.test(body?.message ?? '');
		return frozen ? { kind: 'frozen', message: body?.message ?? '' } : { kind: 'conflict', message: body?.message ?? '' };
	}
	return { kind: 'error', message: body?.message ?? '' };
};

const useInvalidatePlan = (electionId: number, partyId: number | null) => {
	const queryClient = useQueryClient();
	return () => {
		void queryClient.invalidateQueries({ queryKey: strongHoldKeys.plan(electionId, partyId ?? 0) });
		void queryClient.invalidateQueries({ queryKey: strongHoldKeys.estimates(electionId, partyId ?? 0) });
		void queryClient.invalidateQueries({ queryKey: strongHoldKeys.history(electionId, partyId ?? 0) });
		// Live Election reads the same rows (Strong ones are its strongholds).
		void queryClient.invalidateQueries({ queryKey: liveElectionKeys.strongholds(electionId, partyId ?? 0) });
		void queryClient.invalidateQueries({ queryKey: liveElectionKeys.summaries(electionId) });
	};
};

/** Partial save: only the areas in `ops` change. Problems come back as a typed outcome, never a throw. */
export function usePatchStrongHold(electionId: number, partyId: number | null) {
	const invalidate = useInvalidatePlan(electionId, partyId);
	return useMutation({
		mutationFn: async (input: { expectedVersion: number; changeNote: string | null; ops: IPatchOp[] }): Promise<SaveOutcome> => {
			try {
				const { data } = await coreApi.patch(`/api/v1/LiveElection/${electionId}/strongholds${withParty(partyId)}`, input);
				return { kind: 'saved', result: data?.data as IChangeResult };
			} catch (err) {
				return classifyWrite(err);
			}
		},
		onSuccess: (outcome) => {
			if (outcome.kind === 'saved' || outcome.kind === 'conflict' || outcome.kind === 'frozen') invalidate();
		},
	});
}

export type SettingsOutcome =
	| { kind: 'saved' }
	| { kind: 'invalid'; problems: string[] }
	| Exclude<SaveOutcome, { kind: 'saved' | 'invalid' }>;

export function useSaveStrongHoldSettings(electionId: number, partyId: number | null) {
	const invalidate = useInvalidatePlan(electionId, partyId);
	return useMutation({
		mutationFn: async (
			input: ISettingsInput & { expectedVersion: number; changeNote: string | null }
		): Promise<SettingsOutcome> => {
			try {
				await coreApi.put(`/api/v1/LiveElection/${electionId}/strongholds/settings${withParty(partyId)}`, input);
				return { kind: 'saved' };
			} catch (err) {
				const status = httpStatusOf(err);
				const body = bodyOf(err);
				if (status === 400) return { kind: 'invalid', problems: (body?.data as { problems?: string[] })?.problems ?? [] };
				const outcome = classifyWrite(err);
				return outcome.kind === 'saved' || outcome.kind === 'invalid' ? { kind: 'error', message: '' } : outcome;
			}
		},
		onSuccess: (outcome) => {
			if (outcome.kind !== 'error' && outcome.kind !== 'invalid') invalidate();
		},
	});
}

export function useReferenceElections(electionId: number, partyId: number | null, enabled = true) {
	return useQuery({
		queryKey: strongHoldKeys.references(electionId, partyId ?? 0),
		queryFn: async ({ signal }): Promise<IReferenceElection[]> => {
			const { data } = await coreApi.get(
				`/api/v1/LiveElection/${electionId}/strongholds/reference-elections${withParty(partyId)}`,
				{ signal }
			);
			return (data?.data as IReferenceElection[]) ?? [];
		},
		enabled: enabled && electionId > 0,
		staleTime: 10 * 60_000,
		retry: retryUnlessAbsent,
	});
}

export function useCopyStrongHold(electionId: number, partyId: number | null) {
	const invalidate = useInvalidatePlan(electionId, partyId);
	return useMutation({
		mutationFn: async (input: {
			dryRun: boolean;
			sourceElectionId: number;
			expectedVersion: number;
			mode: 'Merge' | 'Replace';
			include: { tiers: boolean; turnout: boolean; labels: boolean; settings: boolean };
		}): Promise<{ kind: 'ok'; result: ICopyResult } | Exclude<SaveOutcome, { kind: 'saved' | 'invalid' }>> => {
			const params = new URLSearchParams();
			if (input.dryRun) params.set('dryRun', 'true');
			try {
				const { data } = await coreApi.post(`/api/v1/LiveElection/${electionId}/strongholds/copy${withParty(partyId, params)}`, {
					sourceElectionId: input.sourceElectionId,
					expectedVersion: input.expectedVersion,
					mode: input.mode,
					include: input.include,
				});
				return { kind: 'ok', result: data?.data as ICopyResult };
			} catch (err) {
				const outcome = classifyWrite(err);
				return outcome.kind === 'saved' || outcome.kind === 'invalid' ? { kind: 'error', message: '' } : outcome;
			}
		},
		onSuccess: (outcome, input) => {
			if (!input.dryRun && outcome.kind === 'ok') invalidate();
		},
	});
}

export function useLockStrongHold(electionId: number, partyId: number | null) {
	const invalidate = useInvalidatePlan(electionId, partyId);
	return useMutation({
		mutationFn: async () => {
			await coreApi.post(`/api/v1/LiveElection/${electionId}/strongholds/freeze${withParty(partyId)}`);
		},
		onSettled: invalidate,
	});
}

export function useStrongHoldHistory(electionId: number, partyId: number | null, enabled = true) {
	return useInfiniteQuery({
		queryKey: strongHoldKeys.history(electionId, partyId ?? 0),
		queryFn: async ({ pageParam, signal }): Promise<IHistoryPage> => {
			const params = new URLSearchParams({ take: '100' });
			if (pageParam) params.set('before', String(pageParam));
			const { data } = await coreApi.get(`/api/v1/LiveElection/${electionId}/strongholds/history${withParty(partyId, params)}`, {
				signal,
			});
			return data?.data as IHistoryPage;
		},
		initialPageParam: null as number | null,
		getNextPageParam: (last) => last?.nextBefore ?? null,
		enabled: enabled && electionId > 0,
		staleTime: 60_000,
		retry: retryUnlessAbsent,
	});
}
